import React from 'react';
import { ExtendedMarkdown } from '../ExtendedMarkdown';
import { ErrorBoundary } from './ErrorBoundary';
import { ScaledToFit } from './ScaledToFit';
import { ExpandableContainer } from './ExpandableContainer';
import { useDebouncedValue } from './useDebouncedValue';

interface MarkdownWrapperProps {
  content: string;
  title?: string;
  minimizedHeight?: string;
  designWidth?: number;
  maxScale?: number;
  enableScreenshot?: boolean;
  enablePDF?: boolean;
  pdfFilename?: string;
}

const Skeleton = () => {
  return (
    <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
      <div className="animate-pulse">
        {/* Content skeleton */}
        <div className="mb-6">
          <div className="mb-2 h-8 w-48 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-64 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>

        {/* Text skeleton */}
        <div className="space-y-3">
          <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    </div>
  );
};

const MarkdownRenderer = ({ content }: { content: string }) => {
  return (
    <div>
      <ExtendedMarkdown content={content} />
    </div>
  );
};

export const MarkdownWrapper: React.FC<MarkdownWrapperProps> = ({
  content,
  title = 'Content',
  minimizedHeight = '350px',
  designWidth = 900,
  maxScale = 1,
  enableScreenshot = true,
  enablePDF = true,
  pdfFilename = 'content-report.pdf',
}) => {
  const debouncedContent = useDebouncedValue(content, 300, 500);

  return (
    <ExpandableContainer
      title={title}
      minimizedHeight={minimizedHeight}
      enableScreenshot={enableScreenshot}
      enablePDF={enablePDF}
      pdfFilename={pdfFilename}
      printMode="text"
    >
      {(isExpanded) => (
        <ScaledToFit
          designWidth={designWidth}
          maxScale={maxScale}
          enabled={!isExpanded}
          className="p-4"
        >
          <ErrorBoundary
            onError={(error, errorInfo) => {
              console.error('Markdown rendering error:', error, errorInfo);
            }}
            resetKey={debouncedContent + isExpanded}
            fallback={<Skeleton />}
          >
            <MarkdownRenderer content={debouncedContent} />
          </ErrorBoundary>
        </ScaledToFit>
      )}
    </ExpandableContainer>
  );
};
